import { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import type { Branch } from '@/types';

export const useBranches = () => {
    const [branches, setBranches] = useState<Branch[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchBranches = async () => {
        try {
            setLoading(true);
            // Demo mode

            const q = query(collection(db, 'branches'));
            const querySnapshot = await getDocs(q);
            const fetchedBranches = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate() || new Date(),
            })) as Branch[];

            setBranches(fetchedBranches);
            setError(null);
        } catch (err) {
            console.error('Error fetching branches:', err);
            setError('Failed to load branches');
        } finally {
            setLoading(false);
        }
    };

    const createBranch = async (branchData: Omit<Branch, 'id' | 'createdAt'>) => {
        try {

            const docRef = await addDoc(collection(db, 'branches'), {
                ...branchData,
                createdAt: Timestamp.now()
            });
            await fetchBranches();
            return docRef.id;
        } catch (err) {
            console.error('Error creating branch:', err);
            throw err;
        }
    };

    const updateBranch = async (id: string, updates: Partial<Branch>) => {
        try {

            const docRef = doc(db, 'branches', id);
            await updateDoc(docRef, updates);
            await fetchBranches();
        } catch (err) {
            console.error('Error updating branch:', err);
            throw err;
        }
    };

    const deleteBranch = async (id: string) => {
        try {

            await deleteDoc(doc(db, 'branches', id));
            await fetchBranches();
        } catch (err) {
            console.error('Error deleting branch:', err);
            throw err;
        }
    };

    useEffect(() => {
        fetchBranches();
    }, []);

    return {
        branches,
        loading,
        error,
        createBranch,
        updateBranch,
        deleteBranch,
        refreshBranches: fetchBranches
    };
};
